import BaseModel from "nexia_mobile/models/base_model";
import Formatter from "nexia_mobile/formatter";

export default class Schema extends BaseModel {
  static isEphemeral = true;

  nestedCollections() {
    return {
      children: {
        model: Schema,
      },
      childSchemas: {
        model: Schema,
      },
    };
  }

  onParse(rawAttrs) {
    this.get("children").reset();
    this.get("childSchemas").reset();

    const attrs = {
      ...super.onParse(rawAttrs),
    };

    this.validations = {};
    _.each(attrs.fields, (field) => {
      this.validations[field.name] = field;
      const clampedField = this._clampField(field);
      attrs[clampedField.name] = clampedField.value;
    });

    this.schemaFields = attrs.fields;
    delete attrs.fields;

    this._serverErrors = attrs.serverErrors;
    delete attrs.serverErrors;

    return attrs;
  }

  parseLinksNext(links) {
    const link = _.first(links);
    return { nextHref: link ? link.href : null };
  }

  hasNextStep() {
    return Boolean(this.get("nextHref"));
  }

  getNextStep(fetchOpts = {}) {
    if (!this.hasNextStep()) {
      return Promise.reject(
        new Error(
          "Attempted to go to the next step with a Schema that had no next step"
        )
      );
    }
    const nextSchema = new Schema();
    nextSchema.setUrl(this.get("nextHref"));
    nextSchema.setUrlParams(this.getUrlParams());
    // at times fetch may return a response
    return nextSchema.fetch(fetchOpts).then((response) => {
      return { nextSchema, response };
    });
  }

  prepare() /* returns Promise<Schema> */ {
    if (this.hasUrl()) {
      if (this._storedOnServer) {
        const newSchema = new Schema();
        newSchema.setUrl(this.url());
        return newSchema
          .save(
            {},
            {
              method: "POST",
              validate: false,
            }
          )
          .then(() => newSchema);
      } else {
        return this.fetch().then(() => this);
      }
    } else {
      return Promise.resolve(this);
    }
  }

  setStoredOnServer(yesno) {
    this._storedOnServer = yesno;
  }

  setForExistingResource(yesno) {
    this._forExistingResource = yesno;
  }

  isNew() {
    return !this._forExistingResource;
  }

  parseLinksChild(links) {
    return _.each(links, (link) => {
      if (link.hasData()) {
        this.get("children").add(new Schema(link.getData(), { parse: true }));
      }
    });
  }

  parseLinksChildSchema(links) {
    return _.each(links, (link) => {
      if (link.hasData()) {
        this.get("childSchemas").add(
          new Schema(link.getData(), { parse: true })
        );
      }
    });
  }

  triggerValidationErrors(errors) {
    this.trigger("validated", this, errors);
  }

  getUrlParams() {
    let params = {};
    _.each(this.schemaFields, (field) => {
      let value = this.get(field.name);
      if (value != null) {
        if (typeof value === "object" && !Array.isArray(value)) {
          value = JSON.stringify(value); // url encoding doesn't work on objects
        }
        params[field.name] = value;
      }
    });
    return params;
  }

  hasSchemaFields() {
    return _.any(this.schemaFields);
  }

  getSchemaFields() {
    return this.schemaFields || [];
  }

  getServerErrors() {
    return this._serverErrors || {};
  }

  clearServerErrors() {
    this._serverErrors = {};
  }

  hasChildren() {
    return this.get("children").any();
  }

  getChildren() {
    return this.get("children");
  }

  hasChildSchemas() {
    return this.get("childSchemas").any();
  }

  getChildSchemas() {
    return this.get("childSchemas");
  }

  parseLinksCommit(links) {
    let commitModel = this.get("commitModel");
    if (commitModel) {
      this.stopListening(commitModel);
    }

    let link = _.first(links);
    if (link) {
      commitModel = new Schema(link.getData(), { parse: true });
      commitModel.setForExistingResource(link.isPutLink());
    } else {
      commitModel = null;
    }

    return { commitModel };
  }

  canCommit() {
    return Boolean(this.get("commitModel"));
  }

  commit(options) {
    if (!this.isValid()) {
      return Promise.reject(new Error("Cannot commit an invalid schema"));
    }

    if (!this.canCommit()) {
      return Promise.reject(
        new Error("Cannot commit a schema that doesn't have a commit link")
      );
    }

    let commitModel = this.get("commitModel");
    commitModel.setUrlParams(this.getUrlParams());
    let fieldKeys = _.pluck(this.schemaFields, "name");
    commitModel.set(_.pick(this.attributes, fieldKeys));
    return commitModel.save(null /* attrs */, options).then((passedData) => {
      let data = passedData;
      if (data.result) {
        data = data.result;
      }
      if (data.polling_path) {
        this.set("statusUrl", data.polling_path);
      }

      return data;
    });
  }

  canDestroy() {
    return Boolean(this.parentModel && this.parentModel.destroy);
  }

  destroy(options = {}) {
    options.wait = true;
    if (this.parentModel) {
      return this.parentModel.destroy(options);
    } else {
      return undefined;
    }
  }

  _clampField(field) {
    let value;
    let min;
    let max;
    let newValue;
    let sorted;
    if (field != null && _.isNumber(field.value)) {
      value = field.value;
      min = field.min == null ? value : field.min;
      max = field.max == null ? value : field.max;
      newValue = () => sorted[1];
    } else if (field != null && Formatter.isISODate(field.value)) {
      value = Formatter.getDateObjectFromISODate(field.value);
      min = field.min ? Formatter.getDateObjectFromISODate(field.min) : value;
      max = field.max ? Formatter.getDateObjectFromISODate(field.max) : value;
      newValue = () => Formatter.getISODateString(sorted[1]);
    } else {
      return field;
    }

    sorted = _.sortBy([min, value, max], (val) => val);
    field.value = newValue();
    return field;
  }
}



// WEBPACK FOOTER //
// ./src/models/schema.js